import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Image } from "primereact/image";

import lightbulb from "../../../assets/images/lightbulb.png";
import useGetToken from "../../../store/selectors/get-token";
import FloatLabelInput from "../../../components/FloatingLabel";
import FloatingSelect from "../../../components/FloatingSelect";
import BottomMessagePopUp from "../../../components/BottomMessagePopUp";
import useGetVehicleInfo from "../../../store/selectors/get-vehicle-info";
import useStoreVehicleInfo from "../../../store/actions/store-vehicle-info";
import useGetCarPlan from "../../../store/selectors/get-car-plan-quote";
import useGetCarAddOnSelection from "../../../store/selectors/get-car-add-on-selection";
import useStoreCarPlan from "../../../store/actions/store-car-plan-quote";

import postcodeData from '../../../assets/data/postcode.json';
import {
	getQuotesGen,
	getQuotesLib,
	getQuotesRhb,
	getQuotesSompoMqt,
	getQuotesTmim,
	getQuotesLonpac,
	getQuotesTuneProtect,
	getQuotesIkhlas,
	generateQuotationIkhlas,
	viewPolicy,
} from "../../../utils/backend";
import { useTranslation } from "react-i18next";

const ClientInfo = () => {
	const [loading, setLoading] = useState(false);
	const token = useGetToken();
	const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState("");
	const [isErrorVisible, setIsErrorVisible] = useState(false);
	const showErrorMessage = (msg) => {
		setErrorMessage(msg);
		setIsErrorVisible(true);
	};
	const selectedPlan = useGetCarPlan();
    const vehicleInfo = useGetVehicleInfo();
    const setVehicleInfo = useStoreVehicleInfo();
	const addOnSelection = useGetCarAddOnSelection();
	const storeCarPlan = useStoreCarPlan();

	const { t } = useTranslation();

	const [no_of_driver, setNoOfDriver] = useState(2);

	const [lonpacCart, setLonpacCart] = useState(() => {
		const cachedCart = localStorage.getItem("cart_selection");
		return cachedCart
			? JSON.parse(cachedCart)
			: {
					name: "CART 7 DAYS @ RM50.00/DAY",
					code: "7x50",
					si: "350",
					sum_insured: "350",
					unit: "7",
					amount: "50",
			  };
	});

	const [lonpacWindscreenSI, setLonpacWindscreenSI] = useState(() => {
		const cachedCart = localStorage.getItem("M02_sum_insured");
		return cachedCart ? JSON.parse(cachedCart) : {name: "WINDSCREEN", si: "0", unit: "0"};
	});

	const [selectedVariant, setSelectedVariant] = useState(() => {
		const cachedVariant = localStorage.getItem("selectedVariant");
		return cachedVariant ? JSON.parse(cachedVariant) : null;
	});
	
	const [roadTaxOption, setRoadTaxOption] = useState(() => {
		const cachedOption = localStorage.getItem("road_tax_option_api");
		return cachedOption ? cachedOption : "";
	});

	const defaultDrivers = [{ code: "01", name: vehicleInfo?.owner_name || "THE POLICY HOLDER" }];

	const [drivers, setDrivers] = useState(() => {
		const cachedDrivers = localStorage.getItem("drivers");
		return cachedDrivers ? JSON.parse(cachedDrivers) : [];
	});

    const stateOption = [
        { value: "Johor", label: "Johor" },
        { value: "Kedah", label: "Kedah" },
        { value: "Kelantan", label: "Kelantan" },
        { value: "Melaka", label: "Melaka" },
        { value: "Negeri Sembilan", label: "Negeri Sembilan" },
        { value: "Pahang", label: "Pahang" },
        { value: "Penang", label: "Penang" },
        { value: "Perak", label: "Perak" },
        { value: "Perlis", label: "Perlis" },
        { value: "Sabah", label: "Sabah" },
        { value: "Sarawak", label: "Sarawak" },
        { value: "Selangor", label: "Selangor" },
        { value: "Terengganu", label: "Terengganu" },
        { value: "Kuala Lumpur", label: "Kuala Lumpur" }, // Federal Territory
        { value: "Labuan", label: "Labuan" }, // Federal Territory
        { value: "Putrajaya", label: "Putrajaya" } // Federal Territory
    ];

	const stateMapping = {
		"wp kuala lumpur": "Kuala Lumpur",
		"wilayah persekutuan kuala lumpur": "Kuala Lumpur",
		"wp labuan": "Labuan",
		"wilayah persekutuan labuan": "Labuan",
		"wp putrajaya": "Putrajaya",
		"wilayah persekutuan putrajaya": "Putrajaya"
	};
  
	const [adddriver, setAddDriver] = useState(() => {
		const cachedDrivers = localStorage.getItem("adddriver");
		return cachedDrivers ? JSON.parse(cachedDrivers) : [];
	});

	const findStateByPostcode = (postcode) => {
		for (const stateEntry of postcodeData.state) {
			for (const city of stateEntry.city) {
				if (city.postcode.includes(postcode)) {
					const originalState = stateEntry.name.trim().toLowerCase();
					const normalizedState = stateMapping[originalState] || stateEntry.name;
	
					const state = stateOption.find((s) => s.value === normalizedState);
					return state?.value || null;
				}
			}
		}
		return null;
	};

	const [tin_number, setTinNumber] = useState("");
	const [sst_number, setSstNumber] = useState("");

	const [address1, setAddress1] = useState("");
	const [address2, setAddress2] = useState("");
	const [state, setState] = useState(findStateByPostcode(vehicleInfo.postcode));
	const [city, setCity] = useState("");
	const [postcode, setPostcode] = useState(vehicleInfo.postcode);

	useEffect(() => {
		const cachedAddress = localStorage.getItem("cachedAddress");
		if (cachedAddress) {
			const { address1, address2, state, city, postcode } =
				JSON.parse(cachedAddress);
			setAddress1(address1 || "");
			setAddress2(address2 || "");
			setState(state || "");
			setCity(city || "");
			setPostcode(postcode || "");
		}

		if (selectedPlan) {
			setTinNumber(selectedPlan.tin_number ?? "");
			setSstNumber(selectedPlan.sst_number ?? "");
		}
	}, []);

    const handleStateChange = (e) => {
        setState(e.target.value);
    };

	const handlePostcodeChange = (e) => {
		const value = e.target.value;
	
		// Only allow numbers and ensure it doesn't exceed 5 characters
		const numbersOnly = value.replace(/[^\d]/g, '');
		if (numbersOnly.length <= 5) {
			setPostcode(numbersOnly);
		}
	};

	const isPostcodeValid = (postcode) => {
		return postcode.length === 5;
	};

	const submitSelectedPlan = async (e) => {
		e.preventDefault();

        setLoading(true);
        try {
			const queryParameters = new URLSearchParams(window.location.search);
			const policyId = queryParameters.get("policy_id");

            if (!address1) {
				showErrorMessage("Address 1 is required.");
				return;
			}
			if (!state) {
				showErrorMessage("State is required.");
				return;
			}

			if (!isPostcodeValid(postcode)) {
				showErrorMessage("Please enter a valid postcode (Must be exactly 5 digits).");
				return;
			}

			const cachedAddress = {
                address1,
                address2,
                state,
                city,
                postcode,
            };

            // Save to cache
            localStorage.setItem("cachedAddress", JSON.stringify(cachedAddress));

            setVehicleInfo({
                ...vehicleInfo,
                address1: address1,
                address2: address2,
                state: state,
                city: city,
                postcode: postcode
            });

			const getQuote = await getQuoteFunction();
			console.log(getQuote);
			if (!getQuote?.data) {
				console.log('error')
				showErrorMessage(getQuote?.response?.data?.message);
				return;
			}
	
			if (getQuote?.data?.message) {
				showErrorMessage(getQuote?.data?.message);
				return;
			}
	
			if (getQuote.success) {
				storeCarPlan(
					Array.isArray(getQuote.data)
						? getQuote.data[0]
						: getQuote.data
				);
				//(storeCarPlan(getQuote.data));
				setLoading(false);
				navigate(policyId ? `/get-quote/car-details?policy_id=${policyId}` : `/get-quote/car-details`);
			}
        } catch (error) {
			console.log('error', error)
            showErrorMessage("Something went wrong");
            setLoading(false);
        } finally {
            setLoading(false);
        }
	};

	const getDOBFromId = (idNo) => {
		const regex = /^(\d{6})\d{2}\d{3}(\d{1})$/;
		const match = idNo.match(regex);

		if (!match) {
			throw new Error("Invalid ID number format");
		}

		const birthDate = match[1];
		const genderDigit = parseInt(match[2], 10);

		const yearPrefix = birthDate.substring(0, 2);
		const month = birthDate.substring(2, 4);
		const day = birthDate.substring(4, 6);

		const currentYear = new Date().getFullYear();
		const currentCentury = Math.floor(currentYear / 100) * 100;

		let fullYear;

		if (parseInt(yearPrefix, 10) <= currentYear % 100) {
			fullYear = `20${yearPrefix}`;
		} else {
			fullYear = `19${yearPrefix}`;
		}

		const dob = new Date(`${fullYear}-${month}-${day}`);

		const age = currentYear - dob.getFullYear();

		if (isNaN(dob) || age < 0 || age > 120) {
			throw new Error("Invalid birth year in ID number");
		}

		const gender = genderDigit % 2 === 0 ? "F" : "M";

		const formattedDob = dob.toISOString().split("T")[0];

		return {
			dob: formattedDob,
			gender: gender,
			age: age,
		};
	};

	const getAddDrivers = (drivers) => {
		// Allianz takes the same additional-driver payload shape as Generali (date of birth
		// derived from the NRIC), so both insurers route through this builder.
		if (selectedPlan?.insurer_name === "GENERALI" || selectedPlan?.insurer_name === "ALLIANZ") {
			let id_data;
			if (
				(
					vehicleInfo?.id_type === "nric" || vehicleInfo?.id_type === "NRIC"
				) 
				&& vehicleInfo?.id_no
			) {
				id_data = getDOBFromId(vehicleInfo.id_no);
			}

			// Filter out drivers with blank IC numbers
			const validDrivers = Array.isArray(drivers)
				? drivers.filter((driver) => driver.icnumber && driver.icnumber.trim() !== "")
				: [];

			// If no valid drivers, return policy holder as default
			if (validDrivers.length === 0) {
				return [
					{
						name: vehicleInfo?.owner_name || "THE POLICY HOLDER",
						icnumber: vehicleInfo?.id_no || "",
						drvdob: id_data?.dob || "",
						drvgender: id_data?.gender || "",
						drvrel: "INSD",
					},
				];
			}

			// Otherwise, return only the valid drivers
			return validDrivers;
		}
		return drivers
	}

	const getDrivers = (drivers) => {
		if (selectedPlan.insurer_name !== "TOKIO MARINE") {
			if (!drivers || (Array.isArray(drivers) && drivers.length === 0)) return [{ code: "01", name: vehicleInfo?.owner_name || "THE POLICY HOLDER" }];

			if (drivers[0]?.code === "04") {
				return [{ code: "04", name: "ALL DRIVERS" }];
			} else {
				return [...drivers];
			}
		} else if (selectedPlan.insurer_name === "TOKIO MARINE") {
			if (!drivers) return [];
			if (drivers.length > 0) return drivers;
		}

		return drivers || [];
	};

	const getQuoteFunction = async () => {
		try {
			const queryParameters = new URLSearchParams(window.location.search);
			const policyId = queryParameters.get("policy_id");

			let extraCoverages;
			if (vehicleInfo?.insurer_name === "LIBERTY") {
				extraCoverages = (addOnSelection || [])
					.filter(
						(item) =>
							item.code !== "1" &&
							item.code &&
							item.description &&
							item.sum_insured !== null &&
							item.sum_insured !== undefined &&
							item.code !== "ALD" &&
							item.code !== "AND"
					)
					.map(({ code, description, sum_insured }) => ({
						code: code.replace(/\s+/g, ""),
						description,
						sum_insured: sum_insured
							? sum_insured
							: selectedPlan?.sum_insured,
					}));
			} else {
				extraCoverages = (addOnSelection || [])
					.filter(
						(item) =>
							item.code !== "1" &&
							item.code &&
							item.description &&
							item.sum_insured !== null &&
							item.sum_insured !== undefined &&
							item.code !== "ALD" &&
							item.code !== "AND"
					)
					.map(({ code, description, sum_insured }) => {
						if (code.replace(/\s+/g, "") === "MPA") {
							const selectedPlanCode =
								localStorage.getItem("plan_code");
							return {
								code: code.replace(/\s+/g, ""),
								description,
								sum_insured,
								plan_code: selectedPlanCode
									? JSON.parse(selectedPlanCode).code
									: "",
								no_of_seats: selectedVariant?.variant_seat || "",
							};
						}
						if (code.replace(/\s+/g, "") === "PA*P") {
							const selectedPAnPPlan = localStorage.getItem(
								"plan_code"
							);
							return {
								code: code.replace(/\s+/g, ""),
								description,
								sum_insured,
								plan_code: selectedPAnPPlan
									? JSON.parse(selectedPAnPPlan).code
									: "",
								plan_sum_insured: selectedPAnPPlan
									? JSON.parse(selectedPAnPPlan).sum_insured
									: "",
							};
						}
						if (code.replace(/\s+/g, "") === "112") {
							const selectedCART = localStorage.getItem(
								"sompo_cart_selection"
							);
							return {
								code: code.replace(/\s+/g, ""),
								description,
								sum_insured,
								cart_amount: selectedCART
									? JSON.parse(selectedCART).amount
									: "",
								cart_days: selectedCART
									? JSON.parse(selectedCART).days
									: "",
							};
						}
						return {
							code: code.replace(/\s+/g, ""),
							description,
							sum_insured,
						};
					});
			}

			let payloads;

			if (policyId) {
				const policyDetails = await viewPolicy(token, { id: policyId });
				const policyData = policyDetails?.data || {};
				const motorInsurance = policyData?.motor_insurance || {};

				let id_data;
				if (
					(motorInsurance.id_type === "nric" ||
						motorInsurance.id_type === "NRIC") &&
					motorInsurance.id_no
				) {
					id_data = getDOBFromId(motorInsurance.id_no);
				}

				const today = new Date(
					policyDetails?.policy_end_date || new Date()
				);
				today.setHours(today.getHours() + 32);

				const inception_date = today.toISOString().split("T")[0];
				const expiry_date = new Date(today);
				expiry_date.setFullYear(expiry_date.getFullYear() + 1);
				expiry_date.setDate(expiry_date.getDate() - 1);

				let today_issue = new Date();

				let issue_date = today_issue.toISOString().split("T")[0];

				payloads = {
					usecode: "",
					id_type: motorInsurance?.id_type,
					id_no: motorInsurance?.id_no,
					veh_reg_no: motorInsurance?.reg_no,
					veh_type: motorInsurance?.veh_type,
					adddriver: adddriver,
					policy_holder_name: motorInsurance?.owner_name,
					ownership_type: motorInsurance?.ownership_type,
					gender_code: id_data?.gender || "",
					marital_code: id_data?.age >= 30 ? "M" : "S",
					dob: id_data?.dob || "",
					address1: address1,
					address2: address2,
					state: state,
					city: city,
					postcode: postcode,
					driveexp: "",
					occupation: "M36",
					cover_note_cate: "NWOO",
					issue_date: issue_date,
					inception_date: inception_date,
					expiry_date: expiry_date.toISOString().split("T")[0],
					class_code:
						motorInsurance?.veh_type === "motorcycle" ? "01" : "02",
					vehicle_class_code:
						motorInsurance?.veh_type === "motorcycle" ? "01" : "02",
					coverage_code: "CO",
					vehicle_use: "Q3",
					make_code: motorInsurance?.make_code || "",
					model_code: motorInsurance?.model_code || "",
					capacity: motorInsurance?.capacity || "",
					uom: "CC",
					make_year: motorInsurance?.make_year || "",
					seats: motorInsurance?.seats || "",
					ncd_from_code: "",
					engine_no: motorInsurance?.engine_no || "",
					chassis_no: motorInsurance?.chassis_no || "",
					nvic_code: motorInsurance?.nvic_code || "",
					sum_insured: motorInsurance?.sum_insured || "",
					email: "",
					hphoneno: motorInsurance?.hphoneno || "",
					msv_plan_code: "",
					extra_coverages: extraCoverages || [],
					renewal_policy_no: selectedPlan?.renewal_policy_no || "",
					engine_type: selectedPlan?.engine_type,
					drivers:
						selectedPlan?.insurer_name === "GENERALI" || selectedPlan?.insurer_name === "ALLIANZ"
							? getAddDrivers(adddriver)
							: getDrivers(drivers),
					agree_value: selectedPlan?.agree_value || false,
					market_value: selectedPlan?.market_value || false,
				};
			} else {
				console.log("vehicleInfo:", vehicleInfo);
				console.log("selectedVariant:", selectedVariant);
				console.log("selectedPlan:", selectedPlan);
				console.log("extraCoverages:", extraCoverages);

				let id_data;
				if (
					(vehicleInfo.id_type === "nric" ||
						vehicleInfo.id_type === "NRIC") &&
					vehicleInfo.id_no
				) {
					id_data = getDOBFromId(vehicleInfo.id_no);
				}
				let today_issue = new Date();

				let issue_date = today_issue.toISOString().split("T")[0];
				console.log("id_data:", id_data);

				payloads = {
					usecode: "",
					id_type: vehicleInfo.id_type,
					id_no: vehicleInfo.id_no,
					veh_reg_no: vehicleInfo.reg_no,
					veh_type: vehicleInfo.veh_type,
					adddriver: adddriver,
					policy_holder_name: vehicleInfo.ownership_type === "I" ? vehicleInfo.owner_name : vehicleInfo.companyName,
					ownership_type: vehicleInfo.ownership_type,
					gender_code: id_data?.gender,
					marital_code:
						id_data?.age >= 30
							? "M"
							: id_data?.age < 30
							? "S"
							: "O",
					dob: id_data?.dob,
					address1: address1,
					address2: address2,
					state: state,
					city: city,
					postcode: postcode,
					driveexp: "",
					occupation: "M36",
					cover_note_cate: "NWOO",
					issue_date: issue_date,
					inception_date: vehicleInfo.effective_date,
					expiry_date: vehicleInfo.expiry_date,
					class_code:
						vehicleInfo?.veh_type === "motorcycle" ? "01" : "02",
					vehicle_class_code:
						vehicleInfo?.veh_type === "motorcycle" ? "01" : "02",
					coverage_code: "CO",
					vehicle_use: "Q3",
					make_code: vehicleInfo?.vehicle_details?.make_code || "",
					model_code: vehicleInfo?.vehicle_details?.model_code || "",
					capacity: vehicleInfo?.vehicle_details?.veh_capacity || "",
					uom: "CC",
					make_year: vehicleInfo?.vehicle_details?.make_year || "",
					seats: selectedVariant?.variant_seat || "",
					ncd_from_code: "",
					engine_no: vehicleInfo?.vehicle_details?.engine_no || "",
					chassis_no: vehicleInfo?.vehicle_details?.chassis_no || "",
					nvic_code: selectedVariant?.variant_nvic || "",
					sum_insured: selectedPlan?.sum_insured,
					email: "",
					hphoneno: vehicleInfo?.hphoneno || "",
					msv_plan_code: "",
					extra_coverages: extraCoverages || [],
					engine_type: selectedPlan?.engine_type,
					drivers:
						selectedPlan?.insurer_name === "GENERALI" || selectedPlan?.insurer_name === "ALLIANZ"
							? getAddDrivers(adddriver)
							: getDrivers(drivers),
					agree_value: selectedPlan?.agree_value || false,
					market_value: selectedPlan?.market_value || false,
				};

				console.log("payloads", payloads);
			}

			//console.log("ADD ON: ", payloads.extra_coverages);
			console.log(vehicleInfo?.insurer_name);
			let getQuote;
			if (vehicleInfo?.insurer_name === "RHBI") {
				const rhbPayload = {
					...payloads,
					extra_coverages: [
						...payloads.extra_coverages.map((coverage) => {
							if (coverage.code === "E112") {
								return {
									...coverage,
									description:
										"ENDORSEMENT 112 - COMPENSATION FOR ASSESSED REPAIR TIME (CART)",
									code: "E112",
									cart_days: lonpacCart.unit,
									cart_amount: lonpacCart.amount,
								};
							}
							return coverage;
						}),
					],
				};
				getQuote = await getQuotesRhb(token, rhbPayload);
			}
			else if (vehicleInfo?.insurer_name === "GENERALI") {
				getQuote = await getQuotesGen(token, payloads);
			}
			else if (vehicleInfo?.insurer_name === "BERJAYA SOMPO") {
				getQuote = await getQuotesSompoMqt(token, payloads);
			}
			/*if (vehicleInfo?.insurer_name === "TOKIO MARINE") {
				getQuote = await getQuotesTmim(token, {
					...payloads,
					no_of_driver
				});
			}*/
			else if (vehicleInfo?.insurer_name === "LIBERTY") {
				getQuote = await getQuotesLib(token, {
					auth_token: selectedPlan?.auth_token,
					reference_data: selectedPlan?.reference_data,
					quotation_no: selectedPlan?.quotation_no,
					extra_coverages: selectedPlan?.extra_coverages_list,
					road_tax_option: roadTaxOption,
					engine_type: selectedPlan?.engine_type,
					...payloads,
				});
			}
			else if (vehicleInfo?.insurer_name === "TAKAFUL IKHLAS") {
				if (vehicleInfo.ownership_type === "I") {
					let quotesResponse = await getQuotesIkhlas(token, {
						...payloads,
						sum_insured: selectedPlan?.sum_insured || 0
					});

					if (!quotesResponse?.success) {
						showErrorMessage(quotesResponse?.response?.data?.message || "Failed to get quotes");
						return;
					}

					storeCarPlan(
					  	Array.isArray(quotesResponse.data) ? quotesResponse.data[0] : quotesResponse.data
					);

					getQuote = await generateQuotationIkhlas(token, {
						...payloads,
						sum_insured: quotesResponse?.data?.sum_insured || 0,
						flasContribution: quotesResponse?.data?.flasContribution || null,
					});
				
				} else if (vehicleInfo.ownership_type === "B") {
					let quotesResponse = await getQuotesIkhlas(token, {
						...payloads,
						tin_number,
						sst_number,
						sum_insured: selectedPlan?.sum_insured || 0
					});
				
					if (!quotesResponse?.success) {
						showErrorMessage(quotesResponse?.response?.data?.message || "Failed to get quotes");
						return;
					}

					storeCarPlan(
					  	Array.isArray(quotesResponse.data) ? quotesResponse.data[0] : quotesResponse.data
					);
					
					getQuote = await generateQuotationIkhlas(token, {
						...payloads,
						sum_insured: quotesResponse?.data?.sum_insured || 0,
						flasContribution: quotesResponse?.data?.flasContribution || null,
					});
				}
			} else if (vehicleInfo?.insurer_name === "LONPAC") {
				let reqParam2;
				let SDPAInd;

				const hasSDPAR1 = payloads?.extra_coverages?.some(c => c.code === "R1");
				const hasSDPAR2 = payloads?.extra_coverages?.some(c => c.code === "R2");

				if (hasSDPAR1) {
					reqParam2 = "R1";
					SDPAInd = "Y";
				}
				if (hasSDPAR2) {
					reqParam2 = "R2";
					SDPAInd = "Y";
				}

				if (!hasSDPAR1 && !hasSDPAR2) {
					reqParam2 = "";
					SDPAInd = "N";
				}

				const defaultSi = [ "M01", "M11", "M68", "M63", "M65", "M66", "M67" ];
				const defaultVehicleSi = [ "M03", "M17" ];

				const lonpacPayload = {
					...payloads,
					extra_coverages: [...payloads.extra_coverages
						.filter(c => c.code !== "R1" && c.code !== "R2")
						.map((coverage)=> {
							if (defaultSi.includes(coverage.code)) {
								return {
									...coverage,
									si: "0.00",
									unit: "0.00"
								}
							}
							if (defaultVehicleSi.includes(coverage.code)) {
								return {
									...coverage,
									si: selectedPlan?.sum_insured || "0.00",
									unit: "0.00"
								}
							}
							if (coverage.code === "M51") {
								return {
									...coverage,
									description: lonpacCart.name,
									code: "M51",
									si: lonpacCart.si,
									sum_insured: lonpacCart.si,
									unit: lonpacCart.unit
								}
							}
							if (coverage.code === "M02") {
								return {
									...coverage,
									description: lonpacWindscreenSI.name,
									code: "M02",
									si: lonpacWindscreenSI.si,
									unit: "0.00"
								}
							}
							return {
								...coverage
							};
						})
					],
					reqParam2,
					SDPAInd
				}
				getQuote = await getQuotesLonpac(token, lonpacPayload);
			}
			else if (vehicleInfo?.insurer_name === "TUNE PROTECT") {
				//console.log("TUNE PROTECT");
				//console.log([JSON.parse(localStorage.getItem("car_plan"))]);
				getQuote = await getQuotesTuneProtect(token, {
					...payloads,
					sum_insured: selectedPlan?.sum_insured || 0,
					agree_value: selectedPlan?.agree_value || false,
					market_value: selectedPlan?.market_value || false,
					quote_id: selectedPlan?.quote_ref_no || "",
					quoted_amount: selectedPlan?.total_payable || 0
				});
			}
			else {
				console.log("else");
				console.log([JSON.parse(localStorage.getItem("car_plan"))]);
				getQuote = {
					success: true,
					data: [JSON.parse(localStorage.getItem("car_plan"))],
				};
			}

			return getQuote;
		} catch (error) {
			showErrorMessage("Failed to get updated quote.");
			setLoading(false);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<div className="content">
				<div className="get-quote-container d-m-column" style={{ flex: 1 }}>
					<div className="d-flex flex-column">
						<h3 className="mb-0">{`Kindly insert your address`}</h3>
						<div className="d-flex align-items-center w-100 mt-4">
							<Image className="mr-2" src={lightbulb} height={36} width={36} />
							<span className="formatted-text w-85">
								{`Make sure your postcode matches the one in the initial quote. Changes may affect the final price.`}
							</span>
						</div>
					</div>

					<div className="form-input-get-quote-group">
						<div className="row justify-content-between mx-0">
							<div className="col-lg-6 input-group">
								<FloatLabelInput
									label={
                                        <>
                                            Address Line 1
                                            <span className="required-asterisk">*</span>
                                        </>
                                    }
									value={address1}
									onChange={(e) => setAddress1(e.target.value)}
									uppercase
									placeholder=""
									maxLength={30}
								/>
							</div>

							<div className="col-lg-6 input-group">
								<FloatLabelInput
									label={`Address Line 2 (Optional)`}
									value={address2}
									onChange={(e) => setAddress2(e.target.value)}
									uppercase
									placeholder=""
									maxLength={30}
								/>
							</div>

							<div className="col-lg-6 input-group">
								<FloatingSelect
									label={
                                        <>
                                            State
                                            <span className="required-asterisk">*</span>
                                        </>
                                    }
									options={stateOption}
									value={state}
									onChange={(e) => handleStateChange(e)}
									disabled
								/>
							</div>

                            <div className="row justify-content-between mx-0">
								<div className="col-lg-6 col-sm-6 input-group">
									<FloatLabelInput
										label="City (Optional)"
										value={city}
										onChange={(e) => setCity(e.target.value)} // Correct: use e.target.value
										uppercase
										placeholder=""
									/>
								</div>

								<div className="col-lg-6 col-sm-6 input-group">
									<FloatLabelInput
										label={
                                            <>
                                                Postcode
                                                <span className="required-asterisk">*</span>
                                            </>
                                        }
										value={postcode}
										onChange={handlePostcodeChange}
										placeholder=""
										disabled
									/>
								</div>
							</div>
						</div>

						{selectedPlan.insurer_name === "TAKAFUL IKHLAS" && (
						<>
							<div className="d-flex flex-column">
								<h3 className="mb-0">{t(`autoInsurance.clientInfo.eInvoice`)}</h3>
								<div className="d-flex align-items-center w-100 mt-4">
									<Image className="mr-2" src={lightbulb} height={36} width={36} />
									<span className="formatted-text w-85">
										{t(`autoInsurance.clientInfo.eInvoiceNotes`)}
									</span>
								</div>
							</div>

							<div className="form-input-get-quote-group">
								<div className="row justify-content-between mx-0">
									<div className="col-lg-6 input-group" style={{ textAlign: "left" }}>
										<FloatLabelInput
											label={
												<>
													{t(`autoInsurance.clientInfo.tinNumber`)}
												</>
											}
											value={tin_number}
											onChange={(e) => setTinNumber(e.target.value)}
											uppercase
											placeholder=""
										/>
										<span className="formatted-text">{t(`autoInsurance.clientInfo.tinNumberPlaceholder`)}</span>
									</div>

									<div className="col-lg-6 input-group" style={{ textAlign: "left" }}>
										<FloatLabelInput
											label={
												<>
													{t(`autoInsurance.clientInfo.sstNumber`)}
													{vehicleInfo.ownership_type === "B" && 
														<span className="required-asterisk">*</span>
													}
												</>
											}
											value={sst_number}
											onChange={(e) => setSstNumber(e.target.value)}
											uppercase
											placeholder=""
										/>
										<span className="formatted-text">{t(`autoInsurance.clientInfo.sstNumberPlaceholder`)}</span>
									</div>
								</div>
							</div>
						</>
						)}

						<div style={{ flex: 1 }} />

						<Button
							label="Proceed (4/5)"
							className="btn-primary mt-4"
							disabled={
								!address1 ||
								!state ||
								!postcode ||
								(
									vehicleInfo.ownership_type === "B" &&
									selectedPlan.insurer_name === "TAKAFUL IKHLAS" &&
									(!sst_number)
								)
							}
							loading={loading}
							onClick={(e) => submitSelectedPlan(e)}
						/>
					</div>
				</div>
				{/* BottomMessagePopUp Component */}
				<BottomMessagePopUp
					message={errorMessage}
					type="error"
					isVisible={isErrorVisible}
					onClose={() => setIsErrorVisible(false)}
				/>
			</div>
		</>
	);
};

export default ClientInfo;